import React, { useState, useEffect } from "react";
import { ResponsiveContainer, XAxis, YAxis, LineChart, Line, CartesianGrid } from "recharts";
import WizrFlexLayout from "../../../components/WizrFlexLayout";
import WizrText from "../../../components/WizrText";
import WizrIcon from "../../../components/WizrIcon";
import styled from "styled-components";
import { Card, Input, Radio, RadioChangeEvent, Dropdown, Spin } from "antd";
import WizrButton from "../../../components/WizrButton";
import WizrDropDown from "../../../components/WizrDropDown";
import { PlusOutlined, ArrowLeftOutlined, SearchOutlined, CaretDownOutlined } from '@ant-design/icons';
import CxMinimize from "../../../assets/icons/Cxminimize";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Explore from "../../../assets/icons/Explore";
import { dispatchGetSupersetChartID } from "../../../redux/actions/content";
import { getSupersetCredentials } from "../../../api/helpers/contents";
import CxQuestion from '../../../assets/icons/CxQuestion';
import Incident from '../../../assets/icons/Incident';
import Issue from '../../../assets/icons/Issue';
import Task from '../../../assets/icons/Task';
import { getYearRange } from "../../../utils";
import Hamburger from "../../../assets/icons/Hamburger";
import { getDatasourceId } from "../../../utils/supersetHelpers";
import { getTrendLineDrillDownConfig } from '../../../utils/dateRangeHelpers'
import FilterTags from "../../../components/filters/FilterTags";

// Extracted component for expanded intent dropdown
interface ExpandedIntentDropdownProps {
  expandedIntentTimeRange: string;
  getTimeRangeLabel: (timeRange: string) => string;
  setExpandedIntentTimeRange: (timeRange: string) => void;
  fetchGraphData: (chartIds: any, isFromDropdown: boolean, key: string, isTopSection: boolean) => void;
  chartIds: any;
}

const ExpandedIntentDropdown: React.FC<ExpandedIntentDropdownProps> = ({
  expandedIntentTimeRange,
  getTimeRangeLabel,
  setExpandedIntentTimeRange,
  fetchGraphData,
  chartIds
}) => (
  <Dropdown
    menu={{
      items: [
        { key: 'yearly', label: 'Last 12 Years' },
        { key: 'monthly', label: 'Last 12 Months' },
        { key: 'weekly', label: 'Last 12 Weeks' },
        { key: 'daily', label: 'Last 12 Days' },
        { key: 'present day', label: 'Last 12 Hours' }
      ],
      onClick: ({ key }) => {
        setExpandedIntentTimeRange(key);
        // Call fetchGraphData with the selected drill down interval
        fetchGraphData(chartIds?.data?.data?.dashboards, true, key, false);
      }
    }}
    trigger={['click']}
    placement="bottomRight"
  >
    <div style={{ cursor: 'pointer', background: 'none', border: 'none', borderRadius: 0, padding: 0, fontSize: 12, fontWeight: 500, color: '#222', minWidth: 0, alignItems: 'center', gap: 4, display: 'flex' }}>
      <WizrText type="body2" style={{ fontSize: 12, color: '#222', fontWeight: 500, padding: 0, margin: 0 }}>
        {getTimeRangeLabel(expandedIntentTimeRange)}
      </WizrText>
      <CaretDownOutlined style={{ fontSize: 14, color: '#888', padding: 0, margin: 0 }} />
    </div>
  </Dropdown>
);

const TagsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin: 16px 0 0 0;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 8px;
  /* Optional: hide scrollbar for a cleaner look */
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb #fff;
  &::-webkit-scrollbar {
    height: 6px;
    background: #fff;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
`;

const TagCard = styled.button<{ selected?: boolean }>`
  background: #fff;
  border-radius: 8px;
  border: 1px solid ${({ selected }) => (selected ? '#22C55E' : '#E5E7EB')};
  padding: 16px 22px;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-shadow: 0 1px 6px rgba(16,30,54,0.04);
  transition: border 0.2s;
  flex-shrink: 0; /* Prevent shrinking in scroll */
`;

const TagTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const TagChange = styled.span<{ color: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ color }) => color};
  margin-left: 4px;
`;

const TagScore = styled.div<{ color: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ color }) => color};
  margin-top: 2px;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-top: 18px;
`;

const SummaryCard = styled.div<{ color?: string }>`
  background: #fff;
  border-radius: 8px;
  padding: 12px 18px;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1.5px solid #E5E7EB;
`;

const Badge = styled.span<{ bg: string; color: string }>`
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  margin-left: 8px;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #374151;
  font-weight: 400;
  flex: 1.5;
  text-align: left;
  padding-right: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ValueGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const Value = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const Percentage = styled.span`
  font-size: 13px;
  font-weight: 500;
`;

const SummaryValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    font-size: 13px;
    font-weight: 500;
  }
`;

const SummaryScore = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  flex: 0.8;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SummaryBadge = styled.span<{ bg: string; color: string }>`
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
`;

const SummaryAdd = styled.button`
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  color: #6B7280;
  font-size: 14px;
  display: flex;
  align-items: center;
  padding: 2px;
  margin-left: auto;
  justify-content: flex-end;

  &:hover {
    color: #374151;
  }
`;

const SummaryTable = styled.div`
  width: 100%;
  margin-top: 18px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
`;

const SummaryRowTable = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid #F3F4F6;
  &:last-child {
    border-bottom: none;
  }
`;

interface Conversation {

  id: string;
  title: string;
  priority: string;
  sentiment: string;
  intent: string;
  date: string;
  description: string;
  tagArray: string[];
  type: string;
}

interface TopPositiveDriver {
  Intent: string;
  Count: number;
  SentimentScore: number;
  Percentage: number;
}

interface RecentChange {
  Intent: string;
  CurrentMonthCount: number;
  PreviousMonthCount: number;
  Delta: number;
  IncreasePercentage: number;
  OverallSentimentScore: number;
  PreviousSentimentScore: number;
}

interface FilterValue {
  values: string[];
  columnName: string;
}

interface Filters {
  [key: string]: FilterValue;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_SUPERSET_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'wizr-access-key': process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY_VALUE || ''
  }
});

// Add auth state management
interface AuthState {
  accessToken: string | null;
  csrfToken: string | null;
  lastAuthTime: number | null;
}

// Token expiration time (5 minutes)
const TOKEN_EXPIRATION_TIME = 5 * 60 * 1000;

const getAuthHeaders = async () => {
  const currentTime = Date.now();

  // Try to get tokens from localStorage
  const storedAuth = {
    accessToken: localStorage.getItem('superset_access_token'),
    csrfToken: localStorage.getItem('superset_csrf_token'),
    lastAuthTime: Number(localStorage.getItem('superset_last_auth_time'))
  };

  // Check if we have valid tokens in localStorage
  if (
    storedAuth.accessToken &&
    storedAuth.csrfToken &&
    storedAuth.lastAuthTime &&
    (currentTime - storedAuth.lastAuthTime) < TOKEN_EXPIRATION_TIME
  ) {
    return {
      'Authorization': `Bearer ${storedAuth.accessToken}`,
      'X-CSRFToken': storedAuth.csrfToken
    };
  }

  // If no valid tokens, get new ones
  const { accessToken, csrfToken } = await getSupersetAuth();
  return {
    'Authorization': `Bearer ${accessToken}`,
    'X-CSRFToken': csrfToken
  };
};

const getSupersetAuth = async () => {
  const currentTime = Date.now();

  // Try to get tokens from localStorage first
  const storedAuth = {
    accessToken: localStorage.getItem('superset_access_token'),
    csrfToken: localStorage.getItem('superset_csrf_token'),
    lastAuthTime: Number(localStorage.getItem('superset_last_auth_time'))
  };

  // Check if we have valid tokens in localStorage
  if (
    storedAuth.accessToken &&
    storedAuth.csrfToken &&
    storedAuth.lastAuthTime &&
    (currentTime - storedAuth.lastAuthTime) < TOKEN_EXPIRATION_TIME
  ) {
    return {
      accessToken: storedAuth.accessToken,
      csrfToken: storedAuth.csrfToken
    };
  }

  try {
    const credentials = await getSupersetCredentials();
    const loginPayload = {
      username: credentials.data.data.username,
      password: credentials.data.data.password,
      provider: 'db',
      refresh: true
    };

    const loginResponse = await api.post(
      process.env.REACT_APP_SUPERSET_LOGIN_API || '',
      loginPayload
    );

    if (!loginResponse.data.access_token) {
      throw new Error('Failed to get access token');
    }

    const csrfResponse = await api.get(
      process.env.REACT_APP_SUPERSET_CSRF_TOKEN_API || '',
      {
        headers: {
          'Authorization': `Bearer ${loginResponse.data.access_token}`
        }
      }
    );

    // Store tokens in localStorage
    localStorage.setItem('superset_access_token', loginResponse.data.access_token);
    localStorage.setItem('superset_csrf_token', csrfResponse.data.result);
    localStorage.setItem('superset_last_auth_time', currentTime.toString());

    return {
      accessToken: loginResponse.data.access_token,
      csrfToken: csrfResponse.data.result
    };
  } catch (error) {
    console.error('Authentication error:', error);
    // Clear stored tokens on error
    localStorage.removeItem('superset_access_token');
    localStorage.removeItem('superset_csrf_token');
    localStorage.removeItem('superset_last_auth_time');
    throw error;
  }
};

type TagType = 'Performance' | 'Browser' | 'Login' | 'Compatability';

const TAGS: TagType[] = ['Browser', 'Login', 'Compatability'];

type CardDataType = {
  change: string;
  changeColor: string;
  score: number;
  scoreColor: string;
};

const defaultCardData: Record<string, CardDataType> = {
  Performance: { change: '↗ 12%', changeColor: '#EF4444', score: 17, scoreColor: '#EF4444' },
  Browser: { change: '↗ 29%', changeColor: '#22C55E', score: 67, scoreColor: '#22C55E' },
  Login: { change: '↘ 17%', changeColor: '#EF4444', score: 45, scoreColor: '#FBBF24' },
  Compatability: { change: '', changeColor: '', score: 67, scoreColor: '#22C55E' }
};


const CustomerIntentsAndSentiments = ({ onClickIntentsandsentiments, fromRange, fromInterval }: any) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get timeRange and interval from location state
  const range = location.state?.timeRange ?? fromRange;
  const interval = location.state?.interval ?? fromInterval;

  // You can now use these values in your component
  console.log("timeRange:", range);
  console.log("interval:", interval);

  const [selectedTab, setSelectedTab] = useState('dashboards');
  const [selectedTimeRange] = useState('monthly');
  const [, setTicketDetails] = useState<any>({
    bugsAndErrors: 0,
    bugsAndErrorsChange: 0,
    sentiment: 0,
    sentimentChange: 0
  });
  const [, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [conversationList, setConversationList] = useState<Conversation[]>([]);
  const [conversationLoading, setConversationLoading] = useState(true);
  const [, setConversationError] = useState<string | null>(null);
  const [, setTotalConversations] = useState(0);
  const selectedIntent = location.state?.intent ?? location.state?.intentName;
  const selectedIntentName = typeof selectedIntent === 'string'
    ? selectedIntent
    : selectedIntent?.Intent ?? selectedIntent?.Category ?? selectedIntent?.name ?? '';
  const allIntents = location.state?.allIntents;
  const filters = (location.state?.filters || {}) as Filters; // Type assertion for filters
  // Add these after your existing location.state declarations
  const selectedFilter = location.state?.selectedFilter ?? "Intent";
  const filterType = location.state?.filterType ?? "";
  const allFilters = location.state?.allFilters ?? {
    positiveFilter: "Intent",
    negativeFilter: "Intent",
    increasesFilter: "Intent",
    decreasesFilter: "Intent"
  };

  // Add useEffect to monitor the filter states
  useEffect(() => {
    console.log("Selected Filter:", selectedFilter);
    console.log("Filter Type:", filterType);
    console.log("All Filters:", allFilters);
  }, [selectedFilter, filterType, allFilters]);
  const [selectedTag, setSelectedTag] = useState('');
  const [, setCardData] = useState<Record<string, CardDataType>>(defaultCardData);
  const [expandedIntent, setExpandedIntent] = useState<string | null>(selectedIntentName ?? null);
  const [intentGraphData, setIntentGraphData] = useState<Record<string, any[]>>({});
  const [graphLoading, setGraphLoading] = useState(false);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [, setPositiveDrivers] = useState<any[]>([]);
  const [, setPerformanceCard] = useState<any>(null);
  const [sentiment, setSentiment] = useState('All');
  const [tagCardsData, setTagCardsData] = useState<any[]>([]);
  const [topIntent] = useState<any>(null);
  const [, setShowClearFilters] = useState(true);
  const [sortOrder, setSortOrder] = useState('latest');
  const [, setTopSectionMinimized] = useState(false);
  const [expandedIntentTimeRange, setExpandedIntentTimeRange] = useState('monthly');
  const [searchTerm, setSearchTerm] = useState('');
  const [allIntentsList, setAllIntentsList] = useState<any[]>([]);
  const [, setAppliedFilters] = useState<Filters>({});

  const getButtonStyle = (value: string) => {
    return {
      backgroundColor: value === selectedTab ? '#000' : '#fff',
      borderColor: '#000',
      color: value === selectedTab ? '#fff' : '#000',
      borderRadius: '20px',
      padding: '8px 8px',
      margin: '0 4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: '1px',
    };
  };
  const getFilterName = () => {
    if (filterType === 'positive') {
      return allFilters.positiveFilter;
    }
    if (filterType === 'negative') {
      return allFilters.negativeFilter;
    }
    if (filterType === 'increases') {
      return allFilters.increasesFilter;
    }
    if (filterType === 'decreases') {
      return allFilters.decreasesFilter;
    }
    return selectedFilter;
  };

  const onTabChange = (e: RadioChangeEvent) => {
    setSelectedTab(e.target.value);
    if (e.target.value === 'discoverSuperset') {
      navigate('/controlroom/discoverSuperset');
    }
  };



  const [chartIds, setChartIds] = useState<any>(null);

  // useEffect(() => {


  //     }, []);



  // Define tag config with order and slice IDs
  // const TAGS_CONFIG = [
  //   { key: 'Performance', sliceId: getChartId('Performance')? getChartId('Performance'):19 },
  //   { key: 'Browser', sliceId: getChartId('Browser')? getChartId('Browser'):17 },
  //   { key: 'Login', sliceId: getChartId('Login')? getChartId('Login'):18 },
  //   { key: 'Compatability', sliceId: getChartId('Compatability')? getChartId('Compatability'):20 }
  // ];

  const getTimeGrain = (interval: string) => {
    switch (interval?.toLowerCase()) {
      case 'daily':
      case '1 day':
        return 'P1D';
      case 'weekly':
      case '1 week':
        return 'P1W';
      case 'monthly':
      case '1 month':
        return 'P1M';
      case 'yearly':
      case '1 year':
        return 'P1Y';
      case 'custom':
        return 'P1D';
      default:
        return 'P1M';  // Default to monthly
    }
  };

  const calculateDaysBetweenDates = (startDate: number, endDate: number): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatRangeForSuperset = (range: { currentMonthStartDate: number; currentMonthEndDate: number }) => {
    // Get end date
    const end = new Date(range.currentMonthEndDate);
    end.setUTCHours(23, 59, 59, 999);

    // Get start date
    const start = new Date(range.currentMonthStartDate);
    start.setUTCHours(0, 0, 0, 0);

    return `${start.toISOString().slice(0, 19)} : ${end.toISOString().slice(0, 19)}`;
  };

  const daysBetween = calculateDaysBetweenDates(range.currentMonthStartDate, range.currentMonthEndDate);

  // Helper function to map filters to Superset format
  const mapFiltersToSuperset = (filters: Filters): any[] => {
    return Object.entries(filters).map(([key, filter]: [string, FilterValue]) => {
      if (key === 'Tags') {
        return {
          col: 'tag_array',
          op: "IN",
          val: filter.values
        };
      }
      if (key === 'Time of Resolution') {
        return {
          col: 'Time_of_Resolution_Filter',
          op: "TEMPORAL_RANGE",
          val: filter.values[0]
        };
      }
      if (key === 'Created_Date') {
        return {
          col: 'Created_Date_Filter',
          op: "IN",
          val: filter.values
        };
      }
      if (key === 'First Response Time') {
        return {
          col: 'First_Response_Time_Filter',
          op: "TEMPORAL_RANGE",
          val: filter.values[0]
        };
      }
      if (key === 'Updated_Date') {
        return {
          col: 'Updated_Date_Filter',
          op: "IN",
          val: filter.values
        };
      }
      return {
        col: filter.columnName,
        op: "IN",
        val: filter.values
      };
    });
  };

  // Helper function to create base payload structure
  const createBasePayload = (
    queryContext: any,
    formData: any,
    datasourceId: string | number | null,
    sliceId: string | number,
    timeGrain: string,
    baseFilters: any[],
    daysBetween: number
  ) => ({
    datasource: {
      ...queryContext.datasource,
      id: datasourceId
    },
    queries: queryContext.queries.map((query: any) => ({
      ...query,
      filters: baseFilters,
      extras: {
        time_grain_sqla: timeGrain,
        having: "",
        where: ""
      },
      time_offsets: [`${daysBetween} days ago`]
    })),
    form_data: {
      ...formData,
      datasource: `${datasourceId}_table`,
      slice_id: sliceId,
      force: false,
      time_grain_sqla: timeGrain,
      result_format: 'json',
      result_type: 'full'
    }
  });

  // Helper function to determine chart type
  const getChartType = (sliceId: number, intentName?: string) => {
    const intentsChartId = getChartId('Intents', chartIds?.data?.data?.dashboards);
    const commonTagsChartId = getChartId('Common Tags by Intent', chartIds?.data?.data?.dashboards);
    const ticketDiscoverChartId = getChartId('Ticket Discover', chartIds?.data?.data?.dashboards);

    const isIntentGraph = sliceId === intentsChartId || (intentName && !chartIds?.data?.data?.dashboards);
    const isCommonTags = !isIntentGraph && sliceId === commonTagsChartId;
    const isTicketDiscover = !isIntentGraph && !isCommonTags && sliceId === ticketDiscoverChartId;

    return { isIntentGraph, isCommonTags, isTicketDiscover };
  };

  // Helper function to build base filters
  const buildBaseFilters = (
    queryContext: any,
    intentName?: string,
    tag?: string,
    sentimentValue?: string,
    isTicketDiscover?: boolean,
    dateRangeFilter?: any
  ) => {
    const selectedFilters = mapFiltersToSuperset(filters);
    
    return [
      ...(queryContext.queries[0].filters || []).filter((f: any) => f.col !== "Created_Date_Filter"),
      ...(intentName ? [{ col: getFilterName(), op: "IN", val: [intentName] }] : []),
      ...(isTicketDiscover && tag ? [{ col: "tag_array", op: "IN", val: [tag] }] : []),
      ...(sentimentValue ? [{ col: "Sentiment", op: "==", val: sentimentValue }] : []),
      ...(dateRangeFilter ? [dateRangeFilter] : []),
      ...selectedFilters
    ];
  };

  const fetchSupersetData = async (
    sliceId: number,
    options: {
      intentName?: string;
      tag?: string;
      sentimentValue?: string;
      orderDirection?: string;
      searchTerm?: string;
      timeRange?: { currentMonthStartDate: number; currentMonthEndDate: number };
      selectedInterval?: string;
    } = {}
  ) => {
    const {
      intentName,
      tag,
      sentimentValue,
      orderDirection = 'desc',
      searchTerm,
      timeRange,
      selectedInterval
    } = options;
    try {
      const headers = await getAuthHeaders();
      const chartUrl = process.env.REACT_APP_SUPERSET_CHART_API?.replace('{chart_id}', String(sliceId)) || '';
      const chartResponse = await api.get(chartUrl, { headers });

      if (chartResponse?.data?.result) {
        const queryContext = JSON.parse(chartResponse.data.result.query_context);
        const formData = JSON.parse(chartResponse.data.result.params);

        const { isIntentGraph, isCommonTags, isTicketDiscover } = getChartType(sliceId, intentName);



        const effectiveRange = isIntentGraph && timeRange ? timeRange : (range || getYearRange());

        const daysBetween = calculateDaysBetweenDates(
          effectiveRange.currentMonthStartDate,
          effectiveRange.currentMonthEndDate
        );

        const dateRangeFilter = {
          col: "Created_Date_Filter",
          op: "TEMPORAL_RANGE",
          val: formatRangeForSuperset(effectiveRange)
        };

        const orderBool = orderDirection === 'asc';

        // Determine the effective interval for time grain
        let effectiveInterval = selectedInterval;
        if (!effectiveInterval) {
          effectiveInterval = isIntentGraph ? 'yearly' : interval;
        }

        // Convert the interval to the proper time grain format
        const timeGrain = getTimeGrain(effectiveInterval || 'yearly');

        const baseFilters = buildBaseFilters(queryContext, intentName, tag, sentimentValue, isTicketDiscover, dateRangeFilter);

        const adhocfilterIntentGraph = [
          {
            expressionType: "SIMPLE",
            subject: getFilterName(),
            operator: "IN",
            comparator: [intentName],
            clause: "WHERE",
          }, {
            expressionType: "SIMPLE",
            subject: "Created_Date_Filter",
            operator: "TEMPORAL_RANGE",
            comparator: formatRangeForSuperset(effectiveRange),
            clause: "WHERE",
          },
          ...Object.values(filters).map(filter => ({
            clause: "WHERE",
            comparator: filter.values,
            operator: "IN",
            subject: filter.columnName
          }))
        ];

        const columnIntentGrpah = [{
          columnType: "BASE_AXIS",
          expressionType: "SQL",
          label: "Created_Date_TS",
          sqlExpression: "Created_Date_TS",
          timeGrain: timeGrain,
          override_time_grain: true
        }];

        const chartIDResponse = await dispatchGetSupersetChartID();
        // Get the dynamic datasource ID from the dashboard config
        const datasourceId = getDatasourceId(chartIDResponse.data);

        const getIntentGraphPayload = () => {
          const payload = createBasePayload(queryContext, formData, datasourceId, sliceId, timeGrain, baseFilters, daysBetween);
          payload.queries = payload.queries.map((query: any) => ({
            ...query,
            columns: columnIntentGrpah
          }));
          payload.form_data.adhoc_filters = adhocfilterIntentGraph;
          return payload;
        };

        const getCommonTagsPayload = () => {
          const payload = createBasePayload(queryContext, formData, datasourceId, sliceId, timeGrain, baseFilters, daysBetween);
          payload.queries = payload.queries.map((query: any) => ({
            ...query,
            columns: ["tag_array"]
          }));
          payload.form_data.columns = ["tag_array"];
          return payload;
        };

        const getTicketDiscoverPayload = () => {
          const payload = createBasePayload(queryContext, formData, datasourceId, sliceId, timeGrain, baseFilters, daysBetween);
          payload.queries = payload.queries.map((query: any) => ({
            ...query,
            extras: {
              ...query.extras,
              where: searchTerm ? `(Ticket_Subject LIKE '%${searchTerm}%' OR Ticket_Priority LIKE '%${searchTerm}%' OR sentiment LIKE '%${searchTerm}%' OR tag_array LIKE '%${searchTerm}%' OR Type LIKE '%${searchTerm}%' OR Ticket_ID LIKE '%${searchTerm}%'  )` : ""
            },
            orderby: [["Created_date_epoch_millis", orderBool]],
            row_limit: 1000
          }));
          return payload;
        };

        const getDefaultPayload = () => {
          const payload = createBasePayload(queryContext, formData, datasourceId, sliceId, timeGrain, baseFilters, daysBetween);
          payload.queries = payload.queries.map((query: any) => ({
            ...query,
            columns: [getFilterName()]
          }));
          payload.form_data.groupby = [getFilterName()];
          return payload;
        };

        const getPayloadForChartType = () => {

          if (isIntentGraph) return getIntentGraphPayload();
          if (isCommonTags) return getCommonTagsPayload();
          if (isTicketDiscover) return getTicketDiscoverPayload();
          return getDefaultPayload();
        };

        const dataResponse = await api.post(process.env.REACT_APP_SUPERSET_CHART_DATA_API || '', getPayloadForChartType(), { headers });

        if (dataResponse.data?.result?.[0]?.data) {
          return dataResponse.data.result[0].data;
        } else {
          throw new Error('No data received from the chart endpoint');
        }
      } else {
        throw new Error('Failed to fetch chart configuration');
      }
    } catch (error: any) {
      console.error(`Error fetching data for slice ${sliceId}:`, error);
      throw error;
    }
  };

  const RightPanel = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 16px 0 0 0;
    min-width: 476px;
    max-width: 440px;
    height: 100%;
    display: flex;
    flex-direction: column;
  `;

  const ConversationHeader = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 24px 16px 24px;
    border-bottom: 1px solid #E5E7EB;
    gap: 12px;
  `;

  const ConversationStats = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 5px 15px;
  `;

  const FilterPath = styled.div`
    font-size: 13px;
    color: #6B7280;
    display: flex;
    align-items: center;
    gap: 8px;

    span {
      color: #6B7280;
    }
  `;

  const ClearFilters = styled.button`
    color: #000;
    font-size: 13px;
    cursor: pointer;
    margin-left: 8px;
    background: none;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    font-weight: 500;
    text-decoration: underline;
  `;

  const ConversationList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0 24px;
  `;

  const ConversationCard = styled(Card)`
    margin-bottom: 8px;
    border-radius: 8px !important;
    border: 1px solid #E5E7EB;
    box-shadow: none;
    background: white;
    .ant-card-body {
      padding: 16px;
      text-align: left;
    }
  `;

  const StatusTag = styled.span`
    background: ${props => props.color};
    color: white;
    font-size: 12px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 4px;
    margin-right: 8px;
  `;

  const IssueTag = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    color: #6B7280;
    font-size: 13px;
    font-weight: 500;
    
    span {
      color: #2563EB;
    }
  `;

  const TicketTag = styled.span`
    background: #F3F4F6;
    color: #374151;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
    border-radius: 16px;
    margin-right: 8px;
  `;

  const StyledDropdown = styled(WizrDropDown)`
    .ant-select-selector {
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
      outline: none !important;
      font-weight: 500 !important;
      font-size: 13px !important;   
      color: #222 !important;
      padding: 0 22px 0 0 !important; 
      min-height: 0 !important;
      height: auto !important;
      display: flex !important;
      align-items: center !important;
    }
    .ant-select-arrow {
      color: #bdbdbd !important;
      right: 4px !important; // <-- Ensure arrow is not overlapping text
    }
    width: auto !important;
  `;

  const cleanValue = (val: any) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') {
      if (val === 'null') return '';
      if (val.startsWith('"') && val.endsWith('"')) return val.slice(1, -1);
    }
    return val;
  };

  const transformSupersetData = (data: any[]) => {
    try {
      if (!Array.isArray(data) || data.length === 0) {
        console.error('Invalid data format:', data);
        return null;
      }

      // Transform the data for the line chart
      const chartData = data.map(item => {
        const date = new Date(item.Created_Date_TS);
        // Format date as DD MMM YYYY
        const formattedDate = date.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
        return {
          date: formattedDate,
          value: item["COUNT(*)"] || item.Count || 0
        };
      }).sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });

      return {
        chartData,
        total: data.reduce((sum, item) => sum + (item["COUNT(*)"] || item.Count || 0), 0),
        change: 0
      };
    } catch (error) {
      console.error('Error transforming Superset data:', error);
      return null;
    }
  };

  const fetchTicketDetails = async (ticketId?: any) => {
    try {
      setLoading(true);
      setError(null);

      console.log('inside fetchTicketDetails', ticketId);
      console.log(`getChartId('Intents')`, getChartId('Intents'));



      // Get the correct chart ID for Intents
      const intentsChartId = getChartId('Intents', ticketId)
      console.log('Fetching ticket details with chartId:', intentsChartId);

      const data = await fetchSupersetData(intentsChartId);
      console.log('Fetched ticket details data:', data);

      if (!data || data.length === 0) {
        // If no data, use the selected intent data if available
        if (selectedIntent) {
          console.log('Using selected intent data:', selectedIntent);
          const transformedData = {
            chartData: [],
            total: selectedIntent.Count || selectedIntent["Count(*)"] || 0,
            change: selectedIntent.PercentageChange || selectedIntent.Percentage || 0
          };
          setTicketDetails(transformedData);
        } else {
          throw new Error('No data available');
        }
      } else {
        const transformedData = transformSupersetData(data);
        if (transformedData) {
          setTicketDetails(transformedData);
        } else {
          throw new Error('Failed to transform data');
        }
      }
    } catch (error: any) {
      console.error('Error in fetchTicketDetails:', error);
      let errorMessage = 'Failed to fetch ticket details. Please try again later.';

      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Authentication failed. Please check your credentials.';
            break;
          case 403:
            errorMessage = 'Access forbidden. Please check your permissions.';
            break;
          case 404:
            errorMessage = 'The requested data could not be found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = error.response.data?.message || errorMessage;
        }
      }

      setError(errorMessage);
      // Set default values instead of zeros
      setTicketDetails({
        bugsAndErrors: selectedIntent?.Count || selectedIntent?.["Count(*)"] || 0,
        bugsAndErrorsChange: selectedIntent?.PercentageChange || selectedIntent?.Percentage || 0,
        sentiment: selectedIntent?.Sentiment || selectedIntent?.SentimentScore || 0,
        sentimentChange: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (chartId?: any) => {
    try {
      setConversationLoading(true);
      setConversationError(null);

      const data = await fetchSupersetData(
        chartId ?? getChartId('Ticket Discover'),
        { intentName: selectedIntentName }
      );

      // Add test data if no data is returned
      const testData = [{
        Ticket_ID: "TCKT-6043",
        Created_Date: "31-07-2024 00:00",
        Ticket_Priority: "High",
        Sentiment: "Positive",
        Ticket_Subject: "Freight Tracking System Failure - Urgent Assistance Required",
        Description: "Hi support, there's a problem with Freight Tracking. The service is either down or causing delays in the EU region. Can you check?",
        Intent: "Custom Reporting",
        Type: "Question",
        Tags: "[\"[\\\"Browser\\\", \\\"Login\\\", \\\"Compatibility\\\"]\"]",
        tag_array: "[\"Browser\", \" Login\", \" Compatibility\"]"
      }];

      const rawData = data.length > 0 ? data : testData;
      const transformedData = transformConversationData(rawData);

      if (transformedData.length === 0) {
        console.warn('Transformed data is empty. Check data transformation logic.');
        setConversationError('No conversations found in the data');
      } else {
        setConversationList(transformedData);
        setTotalConversations(transformedData.length);
        setConversationError(null);
      }
    } catch (error: any) {
      console.error('Error in fetchConversations:', error);
      let errorMessage = 'Failed to fetch conversations. Please try again later.';

      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Authentication failed. Please check your credentials.';
            break;
          case 403:
            errorMessage = 'Access forbidden. Please check your permissions.';
            break;
          case 404:
            errorMessage = 'The conversation data could not be found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = error.response.data?.message || errorMessage;
        }
      }

      setConversationError(errorMessage);
      setConversationList([]);
      setTotalConversations(0);
    } finally {
      setConversationLoading(false);
    }
  };

  const transformConversationData = (data: any[]): Conversation[] => {
    return data.map(item => {
      let tagArray: string[] = [];
      if (Array.isArray(item.tag_array)) {
        tagArray = item.tag_array;
      } else if (typeof item.tag_array === 'string') {
        try {
          tagArray = JSON.parse(item.tag_array);
        } catch {
          tagArray = [];
        }
      }
      return {
        id: cleanValue(item.Ticket_ID),
        title: cleanValue(item.Ticket_Subject),
        priority: cleanValue(item.Ticket_Priority),
        sentiment: cleanValue(item.Sentiment),
        intent: cleanValue(item.Intent),
        date: cleanValue(item.Created_Date),
        description: cleanValue(item.Description),
        tagArray,
        type: cleanValue(item.Type)
      };
    });
  };

  // Function to transform intent data into card data format
  const transformIntentToCardData = (intent: any): CardDataType => {
    let scoreColor: string;
    if (intent.SentimentScore > 55) {
      scoreColor = '#22C55E';
    } else if (intent.SentimentScore > 45) {
      scoreColor = '#FBBF24';
    } else {
      scoreColor = '#EF4444';
    }

    return {
      change: intent.PercentageChange > 0 ? `<WizrIcon name='trendUpRed' /> ${Math.abs(intent.PercentageChange)}%` : `<WizrIcon name='trendDownGreen' /> ${Math.abs(intent.PercentageChange)}%`,
      changeColor: intent.PercentageChange > 0 ? '#22C55E' : '#EF4444',
      score: intent.SentimentScore,
      scoreColor
    };
  };

  // Update card data when selected intent changes
  useEffect(() => {
    if (selectedIntent?.data?.[0]) {
      const intent = selectedIntent.data[0];
      setCardData(prev => ({
        ...prev,
        [intent.Intent]: transformIntentToCardData(intent)
      }));
      setSelectedTag(intent.Intent);
    }
  }, [selectedIntent]);

  // Update card data for all intents
  useEffect(() => {
    if (allIntents) {
      const newCardData = { ...defaultCardData };

      // Process positive intents
      if (allIntents.positive) {
        allIntents.positive.forEach((intent: any) => {
          newCardData[intent.Intent] = transformIntentToCardData(intent);
        });
      }

      // Process negative intents
      if (allIntents.negative) {
        allIntents.negative.forEach((intent: any) => {
          newCardData[intent.Intent] = transformIntentToCardData(intent);
        });
      }

      setCardData(newCardData);
    }
  }, [allIntents]);

  const fetchChartIds = async () => {
    const chartid = await dispatchGetSupersetChartID();
    setChartIds(chartid);
    return chartid
  };

  useEffect(() => {
    if (selectedIntentName || selectedIntent) {
      const init = async () => {
        try {
          const initTicketId = await fetchChartIds();
          console.log('Initialized chart IDs:', initTicketId?.data?.data?.dashboards);

          // Only proceed if we have valid chart IDs
          if (!initTicketId?.data?.data?.dashboards) {
            console.error('Chart IDs not available');
            return;
          }

          // Store dashboards to avoid multiple accesses
          const dashboards = initTicketId.data.data.dashboards;

          // Ensure we have all required chart IDs
          const requiredCharts = ['Intents', 'Common Tags by Intent', 'Ticket Discover'];
          const missingCharts = requiredCharts.filter(chartName => !getChartId(chartName, dashboards));

          if (missingCharts.length > 0) {
            console.error('Missing required chart IDs:', missingCharts);
            return;
          }

          // Make API calls in sequence to ensure proper order
          await fetchTicketDetails(dashboards);
          await fetchCommonTags(dashboards);  // Moved before conversations
          await fetchConversations(dashboards);
          await fetchGraphData(dashboards);

          if (selectedIntentName) {
            try {
              await fetchSupersetData(
                getChartId('Ticket Discover', dashboards),
                {
                  intentName: selectedIntentName,
                  tag: selectedTag
                }
              );
            } catch (e) {
              console.error('Error fetching ticket discover data:', e);
            }
          }
        } catch (error) {
          console.error('Initialization error:', error);
        }
      };
      init();
    }
  }, [range, interval, selectedIntent, selectedIntentName]);

  const getChartId = (chartName: string, allChartId?: any) => {

    console.log('chartIds?.data?.data?.dashboards', chartIds?.data?.data?.dashboards);

    const allIds = allChartId ?? chartIds?.data?.data?.dashboards

    if (!allIds) return null;
    const orgSummaryDashboard = allIds.find(
      (d: any) => d.dashboard_name === "Organization Summary"
    );
    if (!orgSummaryDashboard) return null;
    const chart = orgSummaryDashboard.charts.find(
      (c: any) => c.chart_name === chartName
    );
    console.log('chart', chart);
    return chart?.chart_id;
  };

  const getTimeRangeForInterval = (selectedInterval: string) => {
    const currentDate = new Date();
    let startDate = new Date();
    let endDate = new Date(currentDate);

    switch (selectedInterval?.toLowerCase()) {
      case 'daily':
      case '1 day':
        startDate.setDate(currentDate.getDate() - 1);
        break;
      case 'weekly':
      case '1 week':
        startDate.setDate(currentDate.getDate() - 7);
        break;
      case 'monthly':
      case '1 month':
        startDate.setMonth(currentDate.getMonth() - 1);
        break;
      case 'yearly':
      case '1 year':
        startDate.setFullYear(currentDate.getFullYear() - 1);
        break;
      default:
        startDate.setMonth(currentDate.getMonth() - 1);
    }

    return {
      currentMonthStartDate: startDate.getTime(),
      currentMonthEndDate: endDate.getTime()
    };
  };

  const fetchGraphData = async (chartId?: any, isFromDropdown?: boolean, selectedInterval?: string, isTopSection?: boolean) => {
    setGraphLoading(true);
    try {
      const graphChartId = getChartId('Intents', chartId);

      // Always use monthly (last 12 months) for initial load
      // Ensure effectiveInterval is always a string
      const effectiveInterval: string = isFromDropdown && selectedInterval ? selectedInterval : 'monthly';
      const { timeRange } = getTrendLineDrillDownConfig(effectiveInterval);

      // Rest of the function remains the same
      let intentNameToUse: string | undefined;

      if (isTopSection) {
        intentNameToUse = topIntent?.Intent || selectedIntentName || undefined;
      }
      else if (isFromDropdown && expandedIntent) {
        intentNameToUse = expandedIntent;
      }
      else {
        intentNameToUse = expandedIntent || selectedIntentName || undefined;
      }

      if (!intentNameToUse) {
        console.error('No intent name available');
        return;
      }

      const data = await fetchSupersetData(
        graphChartId,
        {
          intentName: intentNameToUse,
          tag: selectedTag,
          timeRange,
          selectedInterval: effectiveInterval
        }
      );

      if (data && data.length > 0) {
        const formatDateByInterval = (date: Date, interval: string): string => {
          switch (interval.toLowerCase()) {
            case 'yearly':
              return date.getFullYear().toString();
            case 'weekly': {
              const weekNum = Math.ceil((date.getDate() + new Date(date.getFullYear(), date.getMonth(), 1).getDay()) / 7);
              const month = date.toLocaleString('default', { month: 'short' });
              const year = date.getFullYear().toString();
              return `${month} ${year} Week ${weekNum}`;
            }
            case 'daily':
              return date.toLocaleDateString('default', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              });
            case 'present day':
              return date.toLocaleTimeString('default', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              });
            case 'monthly':
            default:
              // Format as "Aug 2024"
              return date.toLocaleString('default', {
                month: 'short',
                year: 'numeric'
              });
          }
        };

        const transformedData = data.map((item: any) => {
          const date = new Date(item.Created_Date_TS);
          const formattedDate = formatDateByInterval(date, effectiveInterval);

          return {
            date: formattedDate,
            value: item["COUNT(*)"]
          };
        });

        const intentKey = String(intentNameToUse);
        setIntentGraphData(prev => ({
          ...prev,
          [intentKey]: transformedData
        }));
      } else {
        const intentKey = String(intentNameToUse);
        setIntentGraphData(prev => ({
          ...prev,
          [intentKey]: []
        }));
      }
    } catch (e) {
      console.error('Error fetching graph data:', e);
      const errorIntentName = isTopSection ?
        (topIntent?.Intent || selectedIntentName || undefined) :
        (expandedIntent || selectedIntentName || undefined);

      if (errorIntentName) {
        const errorIntentKey = String(errorIntentName);
        setIntentGraphData(prev => ({
          ...prev,
          [errorIntentKey]: []
        }));
      }
    }
    console.log('intentGraphData', intentGraphData)
    setGraphLoading(false);
  };

  const fetchCommonTags = async (dashboards: any[]) => {
    try {
      const commonTagsChartId = getChartId('Common Tags by Intent', dashboards);
      if (!commonTagsChartId) {
        console.error("Could not find chart ID for Common Tags by Intent");
        setTagCardsData([]);
        return;
      }

      // Get the selected intent name
      const intentName = selectedIntentName || (selectedIntent?.Intent || selectedIntent?.Category || selectedIntent?.name);

      // Make the API call with the intent filter
      const response = await fetchSupersetData(
        commonTagsChartId,
        {
          intentName, // Pass the intent name as a filter
          timeRange: range
        }
      );

      if (response?.result?.[0]?.data) {
        const filteredData = response.result[0].data.filter(
          (tag: any) => tag.tag_array && tag.tag_array !== "None" && tag.tag_array.trim() !== ""
        );
        setTagCardsData(filteredData);
      } else if (Array.isArray(response)) {
        const filteredData = response.filter(
          (tag: any) => tag.tag_array && tag.tag_array !== "None" && tag.tag_array.trim() !== ""
        );
        setTagCardsData(filteredData);
      } else {
        console.error("Unexpected data structure:", response);
        setTagCardsData([]);
      }
    } catch (error) {
      console.error("Error fetching common tags:", error);
      setTagCardsData([]);
    }
  };

  // Add a useEffect to log tagCardsData changes
  useEffect(() => {
    console.log("Updated tagCardsData:", tagCardsData);
  }, [tagCardsData]);

  // Update useEffect to use new fetchCommonTags
  useEffect(() => {
    if (selectedIntentName || selectedIntent) {
      const init = async () => {
        const initTicketId = await fetchChartIds();
        console.log('effect for fetchticket', initTicketId?.data?.data?.dashboards);
        fetchTicketDetails(initTicketId?.data?.data?.dashboards);
        fetchConversations(initTicketId?.data?.data?.dashboards);
        fetchCommonTags(initTicketId?.data?.data?.dashboards);
        fetchGraphData(initTicketId?.data?.data?.dashboards);
        fetchConversationsForTagAndIntent('Performance', expandedIntent, initTicketId?.data?.data?.dashboards);

        if (selectedIntentName) {
          try {
            await fetchSupersetData(
              getChartId('Ticket Discover', initTicketId?.data?.data?.dashboards),
              {
                intentName: selectedIntentName,
                tag: selectedTag
              }
            );
          } catch (e) {
            console.warn("Error fetching Ticket Discover data:", e);
          }
        }
      };
      init();
    }
  }, [range, interval, selectedIntent, selectedIntentName]);

  // Add another useEffect to refresh tags when selected intent changes
  useEffect(() => {
    if (chartIds?.data?.data?.dashboards) {
      fetchCommonTags(chartIds.data.data.dashboards);
    }
  }, [selectedIntentName, selectedIntent]);

  // Update useEffect for fetching selected intent
  useEffect(() => {
    const fetchSelectedIntent = async () => {
      try {
        const chartNameFromState = location.state?.chartName || 'Top Positive Drivers';
        const chartIdFromState = location.state?.chartId;
        const dynamicChartId = chartIdFromState ?? getChartId(chartNameFromState, chartIds?.data?.data?.dashboards) ?? 12;

        const timeRange = location.state?.timeRange || range;
        const data = await fetchSupersetData(dynamicChartId, { timeRange });
        console.log("data of chart", data);
        // Transform the data to match the expected format
        const transformedData = data.map((item: any) => {
          console.log("Transforming item:", item);  // Log each item being transformed
          return {
            Intent: item.Type || item.Platform || item.Intent || item.Mode_of_Transport || item.Issue_Type || item.Sub_issue_Type || getFilterName(),
            "COUNT(*)": item["COUNT(*)"] || 0,
            "COUNT(*)__366 days ago": item["COUNT(*)__32 days ago"] || 0,
            "Sentiment Metric": item["Sentiment Metric"] || 0,
            "Sentiment Metric__366 days ago": item["Sentiment Metric__32 days ago"] || 0
          };
        });

        console.log("Transformed data:", transformedData);  // Log the transformed data
        setPositiveDrivers(transformedData);

        let selectedIntentData = null;
        if (selectedIntentName) {
          selectedIntentData = transformedData.find((item: any) => {
            const itemIntent = (item.Intent || '').toLowerCase().trim();
            const searchIntent = (selectedIntentName || '').toLowerCase().trim();
            return itemIntent === searchIntent;
          });
        }
        console.log("selectedIntentData", selectedIntentData);


        // Combine all intents into one list with selected intent first
        const combinedIntents = [];
        if (selectedIntentData) {
          combinedIntents.push(selectedIntentData);
        }

        const otherIntents = transformedData.filter((item: any) =>
          (item.Intent || '').toLowerCase().trim() !== (selectedIntentName || '').toLowerCase().trim()
        );

        combinedIntents.push(...otherIntents);
        setAllIntentsList(combinedIntents);

      } catch (e) {
        console.error('Error fetching positive drivers data:', e);
        setAllIntentsList([]);
      }
    };
    fetchSelectedIntent();
  }, [selectedIntent, location.state, range]);

  // Update the calculatePercentageChange function
  const calculatePercentageChange = (current: number, previous: number | null): number => {
    if (!previous || previous === 0) return 0;
    const change = ((current - previous) / previous) * 100;
    return Number.isFinite(change) ? change : 0;
  };

  // Helper function to get sentiment color
  const getSentimentColor = (sentimentScore: number): string => {
    if (sentimentScore > 70) return '#22C55E';
    if (sentimentScore > 50) return '#FBBF24';
    return '#EF4444';
  };

  // Helper function to get sentiment badge text
  const getSentimentBadgeText = (sentimentScore: number): string => {
    if (sentimentScore > 70) return 'GOOD';
    if (sentimentScore > 50) return 'FAIR';
    return 'BAD';
  };

  // Helper function to get trend icon name
  const getTrendIconName = (percentageChange: number) => {
    return percentageChange > 0 ? 'trendUpRed' as const : 'trendDownGreen' as const;
  };

  // Helper function to get trend color
  const getTrendColor = (percentageChange: number): string => {
    return percentageChange > 0 ? '#22C55E' : '#EF4444';
  };

  // Helper function to get time range label
  const getTimeRangeLabel = (timeRange: string): string => {
    if (timeRange === 'yearly') return 'Last 12 Years';
    if (timeRange === 'monthly') return 'Last 12 Months';
    if (timeRange === 'weekly') return 'Last 12 Weeks';
    if (timeRange === 'daily') return 'Last 12 Days';
    return 'Last 12 Months';
  };

  // Helper function to get priority color
  const getPriorityColor = (priority: string): string => {
    if (priority === 'High') return '#EF4444';
    if (priority === 'Medium') return '#FBBF24';
    if (priority === 'Low') return '#22C55E';
    return '#F87171';
  };

  // Helper function to get sentiment status color
  const getSentimentStatusColor = (sentiment: string): string => {
    if (sentiment === 'Negative') return '#EF4444';
    if (sentiment === 'Medium') return '#FBBF24';
    if (sentiment === 'Positive') return '#22C55E';
    return '#F87171';
  };

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const data = await fetchSupersetData(getChartId('Performance') ? getChartId('Performance') : 20);
        // Assume the first item is the performance metric you want
        setPerformanceCard(data && data.length > 0 ? data[0] : null);
      } catch {
        setPerformanceCard(null);
      }
    };
    fetchPerformance();
  }, []);

  // useEffect(() => {
  //   const fetchAllTags = async () => {
  //     const results: Record<string, any> = {};
  //     for (const tag of TAGS_CONFIG) {
  //       try {
  //         const data = await fetchSupersetData(tag.sliceId);
  //         results[tag.key] = data && data.length > 0 ? data : [];
  //       } catch {
  //         results[tag.key] = null;
  //       }
  //     }

  //     setTagCardsData(results);
  //     console.log("tagCardsData",tagCardsData);
  //   };
  //   fetchAllTags();
  // }, []);



  // When a tag is selected, fetch conversations with tag and expanded intent
  const handleTagSelect = (tagKey: string) => {
    setSelectedTag(tagKey);
    // Only fetch ticket discover data without affecting expanded state
    fetchConversationsForTagAndIntent(
      tagKey,
      expandedIntent,
      chartIds?.data?.data?.dashboards,
      sentiment === 'All' ? undefined : sentiment,
      sortOrder
    );
  };



  const toggleIntentExpansion = async (intentName: string, tagOverride?: string) => {
    // If clicking the same intent that's already expanded, collapse it
    if (expandedIntent === intentName) {
      setExpandedIntent(null);
      setFilteredConversations([]);
      setSelectedTag(''); // Reset selected tag when collapsing
      return;
    }

    // Otherwise, expand the new intent
    setExpandedIntent(intentName);
    setGraphLoading(true);

    try {
      // Always use monthly (last 12 months) for initial expansion
      const initialInterval = 'monthly';
      const { timeRange } = getTrendLineDrillDownConfig(initialInterval);

      // Initialize expanded intent time range with monthly
      setExpandedIntentTimeRange(initialInterval);

      // Call for graph data with drill down time range and interval
      const data = await fetchSupersetData(
        getChartId('Intents', chartIds?.data?.data?.dashboards),
        {
          intentName,
          timeRange,
          selectedInterval: initialInterval
        }
      );

      if (data) {
        const transformedData = data.map((item: any) => {
          const date = new Date(item.Created_Date_TS);
          // Always use monthly format for initial expansion
          const formattedDate = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
          return {
            date: formattedDate,
            value: item["COUNT(*)"]
          };
        });

        setIntentGraphData(prev => ({
          ...prev,
          [intentName]: transformedData
        }));
      }
      console.log("intentGraphData", intentGraphData);
      // Fetch common tags for this intent
      const commonTagsChartId = getChartId('Common Tags by Intent', chartIds?.data?.data?.dashboards);
      if (commonTagsChartId) {
        const tagsData = await fetchSupersetData(
          commonTagsChartId,
          {
            intentName,
            timeRange,
            selectedInterval: initialInterval
          }
        );

        if (tagsData) {
          const filteredTagsData = tagsData.filter(
            (tag: any) => tag.tag_array && tag.tag_array !== "None" && tag.tag_array.trim() !== ""
          );
          setTagCardsData(filteredTagsData);
        }
      }

      // Fetch ticket discover data - don't pass tag filter initially
      const ticketData = await fetchSupersetData(
        getChartId('Ticket Discover', chartIds?.data?.data?.dashboards),
        {
          intentName,
          orderDirection: 'desc',
          timeRange,
          selectedInterval: initialInterval
        }
      );

      setFilteredConversations(transformConversationData(ticketData));
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setGraphLoading(false);
    }
  };

  const handleClearAllFilters = () => {
    setExpandedIntent(null);
    setSelectedTag('');
    setFilteredConversations([]);
    setTopSectionMinimized(false); // Show top section when clearing filters
    fetchConversationsForTagAndIntent('', '', chartIds, undefined, 'latest');
    setShowClearFilters(false);

    // Remove selected intent from location state
    navigate(location.pathname, { replace: true, state: { ...location.state, intent: undefined, intentName: undefined } });
  };

  const fetchConversationsForTagAndIntent = async (
    tagName: string,
    intentName: string | null,
    ticketId?: any,
    sentimentValue?: string,
    sortOrder = 'latest',
    searchTerm?: string,
    selectedInterval?: string
  ) => {
    console.log('Fetching conversations with params:', {
      tagName,
      intentName,
      ticketId,
      sentimentValue,
      sortOrder,
      searchTerm,
      selectedInterval
    });

    setConversationLoading(true);
    try {
      let effectiveTimeRange;
      if (selectedInterval === interval) {
        effectiveTimeRange = range;
      } else if (selectedInterval) {
        effectiveTimeRange = getTimeRangeForInterval(selectedInterval);
      } else {
        effectiveTimeRange = range ?? getTimeRangeForInterval(selectedTimeRange);
      }

      const orderDirection = sortOrder === 'latest' ? 'desc' : 'asc';

      console.log('Getting chart ID for Ticket Discover with ticketId:', ticketId);
      const chartId = getChartId('Ticket Discover', ticketId);
      console.log('Retrieved chart ID:', chartId);

      const data = await fetchSupersetData(
        chartId,
        {
          intentName: intentName ?? undefined,
          tag: tagName ?? undefined,
          sentimentValue,
          orderDirection,
          searchTerm,
          timeRange: effectiveTimeRange,
          selectedInterval: selectedInterval || interval || selectedTimeRange
        }
      );

      console.log('Received data from API:', data);
      setFilteredConversations(transformConversationData(data));
      setShowClearFilters(true);
    } catch (e) {
      console.error('Error fetching conversations:', e);
      setFilteredConversations([]);
    }
    setConversationLoading(false);
  };

  const displayedConversations = filteredConversations;

  const SentimentDropdownTrigger = styled.div`
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 500;
    color: #222;
    background: transparent;
    border: none;
    box-shadow: none;
    cursor: pointer;
    padding: 0 8px 0 0;
  `;

  // In your component where the search input is defined
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // If value is empty (cleared), trigger search to reset results
    if (!value) {
      fetchConversationsForTagAndIntent(
        selectedTag,
        expandedIntent,
        chartIds?.data?.data?.dashboards,
        sentiment === 'All' ? undefined : sentiment,
        sortOrder
      );
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchConversationsForTagAndIntent(
        selectedTag,
        expandedIntent,
        chartIds?.data?.data?.dashboards,
        sentiment === 'All' ? undefined : sentiment,
        sortOrder,
        searchTerm
      );
    }
  };




  // Update useEffect to handle initial expansion
  useEffect(() => {
    if (selectedIntentName) {
      setExpandedIntent(selectedIntentName);
      setTopSectionMinimized(false); // Show top section for selected intent
      fetchGraphData(chartIds?.data?.data?.dashboards);
    }
  }, [selectedIntentName]);

  const fetchIntentGraphData = async (chartId: any, intentName?: string, timeRange?: any, selectedInterval?: string) => {
    try {
      const headers = await getAuthHeaders();
      const intentsChartId = getChartId('Intents', chartId);
      const chartUrl = process.env.REACT_APP_SUPERSET_CHART_API?.replace('{chart_id}', String(intentsChartId)) || '';
      const chartResponse = await api.get(chartUrl, { headers });

      if (chartResponse?.data?.result) {
        const queryContext = JSON.parse(chartResponse.data.result.query_context);
        const formData = JSON.parse(chartResponse.data.result.params);
        const effectiveTimeRange = timeRange || range;
        const timeGrain = getTimeGrain(selectedInterval || interval || 'monthly');

        const chartIDResponse = await dispatchGetSupersetChartID();

        const datasourceId = getDatasourceId(chartIDResponse.data);

        const dataResponse = await api.post(process.env.REACT_APP_SUPERSET_CHART_DATA_API || '', {
          datasource: {
            ...queryContext.datasource,
            id: datasourceId
          },
          queries: [{
            ...queryContext.queries[0],
            filters: [
              {
                col: "Created_Date_Filter",
                op: "TEMPORAL_RANGE",
                val: formatRangeForSuperset(effectiveTimeRange)
              },
              ...(intentName ? [{
                col: getFilterName(),
                op: "IN",
                val: [intentName]
              }] : []),
              ...Object.entries(filters).map(([key, filter]: [string, FilterValue]) => {
                if (key === 'Tags') {
                  return {
                    col: 'tag_array',
                    op: "IN",
                    val: filter.values
                  };
                }
                if (key === 'Time of Resolution') {
                  return {
                    col: 'Time_of_Resolution_Filter',
                    op: "TEMPORAL_RANGE",
                    val: filter.values[0]
                  };
                }
                if (key === 'Created_Date') {
                  return {
                    col: 'Created_Date_Filter',
                    op: "IN",
                    val: filter.values
                  };
                }
                if (key === 'First Response Time') {
                  return {
                    col: 'First_Response_Time_Filter',
                    op: "TEMPORAL_RANGE",
                    val: filter.values[0]
                  };
                }
                if (key === 'Updated_Date') {
                  return {
                    col: 'Updated_Date_Filter',
                    op: "IN",
                    val: filter.values
                  };
                }
                return {
                  col: filter.columnName,
                  op: "IN",
                  val: filter.values
                };
              })
            ],
            extras: {
              time_grain_sqla: timeGrain,
              having: "",
              where: ""
            },
            columns: [{
              columnType: "BASE_AXIS",
              expressionType: "SQL",
              label: "Created_Date_TS",
              sqlExpression: "Created_Date_TS",
              timeGrain: timeGrain,
              override_time_grain: true
            }],
            time_offsets: [`${daysBetween} days ago`]
          }],
          form_data: {
            ...formData,
            datasource: `${datasourceId}_table`,
            slice_id: intentsChartId,
            force: false,
            time_grain_sqla: timeGrain,
            result_format: 'json',
            result_type: 'full'
          }
        }, { headers });

        return dataResponse.data?.result?.[0]?.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching intent graph data:', error);
      return [];
    }
  };

  const fetchCommonTagsData = async (chartId: any, intentName?: string) => {
    try {
      const headers = await getAuthHeaders();
      const commonTagsChartId = getChartId('Common Tags by Intent', chartId);
      const chartUrl = process.env.REACT_APP_SUPERSET_CHART_API?.replace('{chart_id}', String(commonTagsChartId)) || '';
      const chartResponse = await api.get(chartUrl, { headers });

      if (chartResponse?.data?.result) {
        const queryContext = JSON.parse(chartResponse.data.result.query_context);
        const formData = JSON.parse(chartResponse.data.result.params);
        const timeGrain = getTimeGrain(interval || 'monthly');

        const chartIDResponse = await dispatchGetSupersetChartID();

        const datasourceId = getDatasourceId(chartIDResponse.data);

        const dataResponse = await api.post(process.env.REACT_APP_SUPERSET_CHART_DATA_API || '', {
          datasource: {
            ...queryContext.datasource,
            id: datasourceId
          },
          queries: [{
            ...queryContext.queries[0],
            filters: [
              {
                col: "Created_Date_Filter",
                op: "TEMPORAL_RANGE",
                val: formatRangeForSuperset(range)
              },
              ...(intentName ? [{
                col: getFilterName(),
                op: "IN",
                val: [intentName]
              }] : []),
              ...Object.entries(filters).map(([key, filter]: [string, FilterValue]) => {
                if (key === 'Tags') {
                  return {
                    col: 'tag_array',
                    op: "IN",
                    val: filter.values
                  };
                }
                if (key === 'Time of Resolution') {
                  return {
                    col: 'Time_of_Resolution_Filter',
                    op: "TEMPORAL_RANGE",
                    val: filter.values[0]
                  };
                }
                if (key === 'Created_Date') {
                  return {
                    col: 'Created_Date_Filter',
                    op: "IN",
                    val: filter.values
                  };
                }
                if (key === 'First Response Time') {
                  return {
                    col: 'First_Response_Time_Filter',
                    op: "TEMPORAL_RANGE",
                    val: filter.values[0]
                  };
                }
                if (key === 'Updated_Date') {
                  return {
                    col: 'Updated_Date_Filter',
                    op: "IN",
                    val: filter.values
                  };
                }
                return {
                  col: filter.columnName,
                  op: "IN",
                  val: filter.values
                };
              })
            ],
            extras: {
              time_grain_sqla: timeGrain,
              having: "",
              where: ""
            },
            columns: ["tag_array"],
            time_offsets: [`${daysBetween} days ago`]
          }],
          form_data: {
            ...formData,
            datasource: `${datasourceId}_table`,
            slice_id: commonTagsChartId,
            force: false,
            time_grain_sqla: timeGrain,
            result_format: 'json',
            result_type: 'full',
            columns: ["tag_array"]
          }
        }, { headers });

        return dataResponse.data?.result?.[0]?.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching common tags data:', error);
      return [];
    }
  };

  const fetchTicketDiscoverData = async (
    chartId: any,
    intentName?: string,
    tag?: string,
    sentiment?: string,
    orderDirection = 'desc',
    searchTerm?: string
  ) => {
    try {
      const headers = await getAuthHeaders();
      const ticketDiscoverChartId = getChartId('Ticket Discover', chartId);
      const chartUrl = process.env.REACT_APP_SUPERSET_CHART_API?.replace('{chart_id}', String(ticketDiscoverChartId)) || '';
      const chartResponse = await api.get(chartUrl, { headers });

      if (chartResponse?.data?.result) {
        const queryContext = JSON.parse(chartResponse.data.result.query_context);
        const formData = JSON.parse(chartResponse.data.result.params);
        const timeGrain = getTimeGrain(interval || 'monthly');

        const chartIDResponse = await dispatchGetSupersetChartID();

        const datasourceId = getDatasourceId(chartIDResponse.data);

        const orderBool = orderDirection === 'asc';

        // Build filters array
        const basefilters = [
          {
            col: "Created_Date_Filter",
            op: "TEMPORAL_RANGE",
            val: formatRangeForSuperset(range)
          },
          ...(intentName ? [{ col: getFilterName(), op: "IN", val: [intentName] }] : []),
          ...(tag ? [{ col: "tag_array", op: "IN", val: [tag] }] : []),
          ...(sentiment ? [{ col: "Sentiment", op: "==", val: sentiment }] : []),
          // Search term filters are commented out
          // ...(searchTerm ? [
          //   { col: "Ticket_Subject", op: "ILIKE", val: `%${searchTerm}%` },
          //   { col: "Description", op: "ILIKE", val: `%${searchTerm}%` }
          // ] : []),
          ...Object.entries(filters).map(([key, filter]: [string, FilterValue]) => {
            if (key === 'Tags') {
              return {
                col: 'tag_array',
                op: "IN",
                val: filter.values
              };
            }
            if (key === 'Time of Resolution') {
              return {
                col: 'Time_of_Resolution_Filter',
                op: "TEMPORAL_RANGE",
                val: filter.values[0]
              };
            }
            if (key === 'Created_Date') {
              return {
                col: 'Created_Date_Filter',
                op: "IN",
                val: filter.values
              };
            }
            if (key === 'First Response Time') {
              return {
                col: 'First_Response_Time_Filter',
                op: "TEMPORAL_RANGE",
                val: filter.values[0]
              };
            }
            if (key === 'Updated_Date') {
              return {
                col: 'Updated_Date_Filter',
                op: "IN",
                val: filter.values
              };
            }
            return {
              col: filter.columnName,
              op: "IN",
              val: filter.values
            };
          })
        ];

        const dataResponse = await api.post(process.env.REACT_APP_SUPERSET_CHART_DATA_API || '', {
          datasource: {
            ...queryContext.datasource,
            id: datasourceId
          },
          queries: [{
            ...queryContext.queries[0],
            filters: dedupeFilters(basefilters),
            extras: {
              time_grain_sqla: timeGrain,
              having: "",
              where: ""
            },
            orderby: [["Created_date_epoch_millis", orderBool]],
            row_limit: 1000,
            time_offsets: [`${daysBetween} days ago`]
          }],
          form_data: {
            ...formData,
            datasource: `${datasourceId}_table`,
            slice_id: ticketDiscoverChartId,
            force: false,
            time_grain_sqla: timeGrain,
            result_format: 'json',
            result_type: 'full'
          }
        }, { headers });

        return dataResponse.data?.result?.[0]?.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching ticket discover data:', error);
      return [];
    }
  };

  // Update the initialization useEffect to use new functions
  useEffect(() => {
    if (selectedIntentName || selectedIntent) {
      const init = async () => {
        try {
          const initTicketId = await fetchChartIds();
          console.log('Initialized chart IDs:', initTicketId?.data?.data?.dashboards);

          if (!initTicketId?.data?.data?.dashboards) {
            console.error('Chart IDs not available');
            return;
          }

          const dashboards = initTicketId.data.data.dashboards;

          // Fetch intent graph data
          const intentGraphData = await fetchIntentGraphData(
            dashboards,
            selectedIntentName
          );
          if (intentGraphData.length > 0) {
            const transformedData = transformSupersetData(intentGraphData);
            setTicketDetails(transformedData);
          }

          // Fetch common tags data
          const commonTagsData = await fetchCommonTagsData(
            dashboards,
            selectedIntentName
          );
          if (commonTagsData.length > 0) {
            const filteredData = commonTagsData.filter(
              (tag: any) => tag.tag_array && tag.tag_array !== "None" && tag.tag_array.trim() !== ""
            );
            setTagCardsData(filteredData);
          }

          // Fetch ticket discover data
          const ticketDiscoverData = await fetchTicketDiscoverData(
            dashboards,
            selectedIntentName,
            selectedTag,
            sentiment === 'All' ? undefined : sentiment,
            sortOrder,
            searchTerm
          );
          if (ticketDiscoverData.length > 0) {
            setFilteredConversations(transformConversationData(ticketDiscoverData));
          }

        } catch (error) {
          console.error('Initialization error:', error);
        }
      };
      init();
    }
  }, [range, interval, selectedIntent, selectedIntentName]);
  console.log("intentGraphData", intentGraphData);

  return (
    <div style={{ minHeight: '100vh', overflow: 'auto', overflowY: 'auto', paddingBottom: '20px', width: '100%' }}>
      <WizrFlexLayout flexDirection='column' alignItems='flex-start' justifyContent='flex-start' style={{ width: '100%', padding: '0 0 8px 0', background: 'transparent', marginBottom: 0 }}>
        <div>
          <WizrFlexLayout flexDirection="row" alignItems="center" justifyContent="space-between" style={{ width: '100%', marginBottom: 16 }}>
            {/* Left: Hamburger + CX Control Room */}

            <button
              onClick={() => navigate('/controlroom/dashboards', { state: { selectedTab: '1' } })}
              style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
              aria-label="Navigate to dashboards"
            >
              <Hamburger />
            </button>
            <WizrText type='h6' style={{ marginLeft: 10 }}>CX Control Room</WizrText>
            {/* Right: Dashboard/Discover */}
            {/* <WizrFlexLayout alignItems='flex-end' flexDirection='row' alignContent='flex-end' justifyContent='center'> */}
            <WizrFlexLayout
              alignItems='center'
              flexDirection='row'
              alignContent='center'
              justifyContent='center'
              style={{ width: '100%', marginLeft: 370, marginTop: 24 }}
            >
              <Radio.Group
                onChange={onTabChange}
                value={selectedTab}
                style={{ marginBottom: 16, display: 'inline-flex' }}
              >
                <Radio.Button
                  value="dashboards"
                  style={getButtonStyle('dashboards')}
                  onClick={() => navigate('/controlroom/dashboards')}
                >
                  Dashboards
                </Radio.Button>
                <Radio.Button
                  className='discoverColumnHead_btn'
                  value="discover"
                  style={getButtonStyle('discover')}
                  onClick={() => navigate('/controlroom/discover')}
                >
                  Discover
                </Radio.Button>
              </Radio.Group>
            </WizrFlexLayout>
          </WizrFlexLayout>
        </div>

        <WizrFlexLayout flexDirection="column" alignItems="flex-start" justifyContent="flex-start" style={{ width: '100%', padding: '0 0 8px 0', background: 'transparent', marginBottom: 0 }}>
          <WizrFlexLayout flexDirection="column" style={{ width: '100%' }}>
            {/* First row: Title and Search */}
            <WizrFlexLayout flexDirection="row" alignItems="center" style={{ width: '100%', marginBottom: 0 }}>
              {/* Left: Arrow + Title */}
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <button
                  onClick={() => navigate('/controlroom/dashboards', { state: { selectedTab: '1' } })}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  aria-label="Go back to dashboards"
                >
                  <ArrowLeftOutlined
                    style={{ fontSize: 14, color: '#222', marginTop: 2 }}
                  />
                </button>
                <WizrText type="sub1" style={{ fontWeight: 600, fontSize: 14, marginLeft: 4 }}>
                  Customer Intents and Sentiments
                </WizrText>
              </div>
              {/* Center: Search Bar */}
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Input
                  placeholder="Search"
                  prefix={<SearchOutlined style={{ color: '#888' }} />}
                  allowClear
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                  style={{
                    width: '424px',
                    borderRadius: '999px',
                    background: '#F8FAFC',
                    border: '1px solid #E5E7EB',
                    height: '38px',
                    fontSize: '13px',
                    paddingLeft: '16px'
                  }}
                />
              </div>
              {/* Right: Empty for now */}
              <div style={{ flex: 1 }} />
            </WizrFlexLayout>

            <WizrFlexLayout>
              <FilterTags
                filters={filters}
                onFilterRemove={(key: string, value: string) => {
                  const newFilters = { ...filters };
                  newFilters[key].values = newFilters[key].values.filter((v: string) => v !== value);
                  if (newFilters[key].values.length === 0) {
                    delete newFilters[key];
                  }
                  setAppliedFilters(newFilters);
                }}
              />

            </WizrFlexLayout>

            {/* Second row: Negative & Explore, right-aligned */}
            <WizrFlexLayout flexDirection="row" justifyContent="flex-end" alignItems="center" style={{ width: '100%', gap: 8, marginBottom: 10 }}>
              <Dropdown menu={{
                items: [
                  { key: 'All', label: '' },
                  { key: 'Positive', label: 'Positive' },
                  { key: 'Negative', label: 'Negative' },
                  { key: 'Neutral', label: 'Neutral' }
                ],
                onClick: ({ key }) => {
                  setSentiment(key);
                  // Call fetchConversationsForTagAndIntent with the selected sentiment
                  fetchConversationsForTagAndIntent(
                    selectedTag,
                    expandedIntent,
                    chartIds?.data?.data?.dashboards,
                    key === 'All' ? undefined : key,  // Don't apply sentiment filter if 'All' is selected
                    sortOrder
                  );
                }
              }} trigger={['click']} placement="bottomRight">
                <SentimentDropdownTrigger>
                  {sentiment}
                  <CaretDownOutlined style={{ fontSize: 13, color: '#bdbdbd', marginLeft: 2 }} />
                </SentimentDropdownTrigger>
              </Dropdown>
              <WizrButton
                text={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 13 }}>
                    <span style={{ fontSize: 10, marginRight: 4, display: 'flex', alignItems: 'center' }}>
                      <Explore />
                    </span>
                    {' '}Explore
                  </span>
                }
                type="default"
                color="#1D1F22"
                onClick={() => navigate('/controlroom/discoverSuperset', {
                  state: {
                    intents: expandedIntent ? [expandedIntent] : [],
                    selectedTab: 'discoverSuperset',
                    defaultFilters: {
                      Sentiment: sentiment !== 'All' ? sentiment : undefined
                    }
                  }
                })}
                style={{
                  borderRadius: '20px',
                  padding: '4px 10px',
                  background: '#EAECF1',
                  border: 'none',
                  fontWeight: 500,
                  fontSize: 15,
                  height: 30
                }}
              />
            </WizrFlexLayout>
          </WizrFlexLayout>

          <WizrFlexLayout flexDirection='row' alignItems='flex-start' style={{ gap: '12px' }}>
            <WizrFlexLayout
              style={{ borderRadius: '8px', flex: 1, background: '#fff', minWidth: 0 }}
              alignContent='flex-start'
              alignItems='flex-start'
              flexDirection='column'
              textAlign='left'
            >
              <StyledTicketCardForCharts>
                <WizrFlexLayout flexDirection="column" style={{ width: '100%', padding: '1px 20px 20px 20px', background: 'white', borderRadius: '8px' }}>
                  <WizrFlexLayout flexDirection="column" style={{ width: '100%', background: 'white', padding: '4px 0' }}>
                    <WizrText type="h4" style={{ fontSize: '13px', fontWeight: '600', color: '#1F1D22', textAlign: 'left', marginBottom: '2px', lineHeight: '37px' }}>
                      {getFilterName()}
                    </WizrText>
                    <div style={{ width: '100%', height: '1px', background: '#E5E7EB', marginBottom: '4px' }} />
                  </WizrFlexLayout>

                  <SummaryTable style={{ padding: '0px 14px' }}>
                    {allIntentsList.map((driver: any, idx: number) => {
                      console.log("driver list", driver);

                      const isExpanded = expandedIntent === driver.Intent;
                      console.log('isExpanded', isExpanded);

                      return (
                        <React.Fragment key={driver.Intent}>
                          {!isExpanded && (
                            <SummaryRowTable style={{ borderBottom: '1px solid #E5E7EB' }}>
                              <SummaryLabel>
                                {driver.Intent || 'Unknown Intent'}
                                <ValueGroup>
                                  <Value>{driver["COUNT(*)"] || 0}</Value>
                                  <Percentage style={{
                                    color: getTrendColor(calculatePercentageChange(driver["COUNT(*)"], driver["COUNT(*)__366 days ago"]))
                                  }}>
                                    <span style={{ fontSize: '12px', width: '12px', height: '12px' }}>
                                      <WizrIcon name={getTrendIconName(calculatePercentageChange(driver["COUNT(*)"], driver["COUNT(*)__366 days ago"]))} />
                                    </span>
                                    {Math.abs(calculatePercentageChange(driver["COUNT(*)"], driver["COUNT(*)__366 days ago"])).toFixed(1)}%
                                  </Percentage>
                                </ValueGroup>
                              </SummaryLabel>
                              <SummaryScore>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>Sentiment Score</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#111827' }}>
                                    <span style={{
                                      color: getSentimentColor(driver["Sentiment Metric"] || 0),
                                      fontWeight: 600
                                    }}>
                                      {(driver["Sentiment Metric"] || 0).toFixed(1)}
                                    </span>
                                    <SummaryBadge
                                      bg={getSentimentColor(driver["Sentiment Metric"] || 0)}
                                      color="#fff"
                                    >
                                      {getSentimentBadgeText(driver["Sentiment Metric"] || 0)}
                                    </SummaryBadge>
                                  </div>
                                </div>
                              </SummaryScore>
                              <SummaryAdd onClick={() => toggleIntentExpansion(driver.Intent)}>
                                <PlusOutlined style={{ fontSize: '14px' }} />
                              </SummaryAdd>
                            </SummaryRowTable>
                          )}
                          {isExpanded && (
                            <WizrFlexLayout flexDirection="column" style={{ width: '100%', background: 'white', padding: '4px 0', marginBottom: '24px' }}>
                              <WizrFlexLayout flexDirection="row" justifyContent="space-between" alignItems="center" style={{ width: '100%', marginBottom: '-1px', background: '#fff', padding: '0 18px 8px 18px', borderRadius: '8px', borderBottom: '1px solid #E5E7EB', minHeight: 48 }}>
                                {/* Left: Intent Name, Count, Trend */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 32, }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 120 }}>
                                    <WizrText type="body2" style={{ color: '#6B7280', fontSize: '12px', fontWeight: 500, marginBottom: 2 }}>
                                      {driver.Intent}
                                    </WizrText>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <WizrText type="h4" style={{ fontSize: '12px', fontWeight: '700', color: '#111827', lineHeight: '1' }}>
                                        {driver["COUNT(*)"] || 0}
                                      </WizrText>
                                      <WizrIcon name={getTrendIconName(calculatePercentageChange(driver["COUNT(*)"], driver["COUNT(*)__366 days ago"]))} />
                                      <span style={{
                                        color: getTrendColor(calculatePercentageChange(driver["COUNT(*)"], driver["COUNT(*)__366 days ago"])),
                                        fontSize: 13,
                                        fontWeight: 600
                                      }}>
                                        {Math.abs(calculatePercentageChange(driver["COUNT(*)"], driver["COUNT(*)__366 days ago"])).toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 120 }}>
                                    <WizrText type="body2" style={{ color: '#6B7280', fontSize: '12px', fontWeight: 500, marginBottom: 2 }}>
                                      Sentiment
                                    </WizrText>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <WizrText type="h4" style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: getSentimentColor(driver["Sentiment Metric"] || 0),
                                        lineHeight: '1'
                                      }}>
                                        {(driver["Sentiment Metric"] || 0).toFixed(1)}
                                      </WizrText>
                                      <SummaryBadge
                                        bg={getSentimentColor(driver["Sentiment Metric"] || 0)}
                                        color="#fff"
                                        style={{ fontWeight: 600, fontSize: 12, padding: '2px 10px', marginLeft: 4 }}
                                      >
                                        {getSentimentBadgeText(driver["Sentiment Metric"] || 0)}
                                      </SummaryBadge>
                                    </div>
                                  </div>
                                </div>
                                {/* Right: Monthly Dropdown and Icons */}
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, marginRight: 0 }}>
                                  <ExpandedIntentDropdown
                                    expandedIntentTimeRange={expandedIntentTimeRange}
                                    getTimeRangeLabel={getTimeRangeLabel}
                                    setExpandedIntentTimeRange={setExpandedIntentTimeRange}
                                    fetchGraphData={fetchGraphData}
                                    chartIds={chartIds}
                                  />
                                  {/* <DownloadOutlined 
                                    style={{ fontSize: 13, color: '#888', cursor: 'pointer' }} 
                                    onClick={() => handleChartDownload(getChartId('Intents', chartIds?.data?.data?.dashboards) ?? 17)}
                                  /> */}
                                  <button
                                    onClick={() => toggleIntentExpansion(driver.Intent)}
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                    aria-label="Minimize intent section"
                                  >
                                    <CxMinimize />
                                  </button>
                                </div>
                              </WizrFlexLayout>
                              {/* Graph Section */}
                              <ResponsiveContainer width="100%" height={220}>
                                {(() => {
                                  if (graphLoading) {
                                    return (
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                        width: '100%',
                                        minHeight: 220
                                      }}>
                                        <Spin size="small" />
                                      </div>
                                    );
                                  }
                                  
                                  if (intentGraphData[driver.Intent]?.length === 0) {
                                    return (
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                        width: '100%',
                                        minHeight: 220,
                                        color: '#6B7280',
                                        fontSize: '14px',
                                        fontWeight: 500
                                      }}>
                                        No data available
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                  <LineChart
                                    data={intentGraphData[driver.Intent] || []}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis
                                      dataKey="date"
                                      axisLine={false}
                                      tickLine={false}
                                      style={{ fontSize: '12px', color: '#6B7280' }}
                                      tickFormatter={date => {
                                        // If date is already formatted (from the transformed data), return as is
                                        if (typeof date === 'string') return date;

                                        const d = new Date(date);
                                        if (isNaN(d.getTime())) return date;

                                        // Format based on the selected time range
                                        switch (expandedIntentTimeRange) {
                                          case 'yearly':
                                            return d.getFullYear().toString();
                                          case 'weekly':
                                            return `Week ${Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7)} ${d.toLocaleString('default', { month: 'short' })}`;
                                          case 'daily':
                                            return d.toLocaleDateString('default', {
                                              day: '2-digit',
                                              month: 'short'
                                            });
                                          case 'monthly':
                                          default:
                                            return d.toLocaleString('default', {
                                              month: 'short',
                                              year: 'numeric'
                                            });
                                        }
                                      }}
                                    />
                                    <YAxis
                                      axisLine={false}
                                      tickLine={false}
                                      style={{ fontSize: '12px', color: '#6B7280' }}
                                      label={{ value: 'Volume', angle: -90, position: 'insideLeft', style: { fill: '#6B7280', fontSize: '12px' } }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="value"
                                      stroke="#FBBF24"
                                      strokeWidth={2}
                                      dot={false}
                                    />
                                  </LineChart>
                                  );
                                })()}
                              </ResponsiveContainer>

                              {/* Common Tags Section for Expanded Intent */}
                              <div style={{ width: '100%', background: 'white', padding: '16px 18px' }}>
                                <WizrText type="sub1" style={{ fontWeight: 500, fontSize: 13, marginBottom: 10, display: 'flex' }}>
                                  Common Tags
                                </WizrText>
                                <TagsRow>
                                  {(() => {
                                    if (graphLoading) {
                                      return (
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          width: '100%',
                                          minHeight: 66,
                                          padding: '20px 0'
                                        }}>
                                          <Spin size="small" />
                                        </div>
                                      );
                                    }
                                    
                                    if (!tagCardsData || tagCardsData.length === 0) {
                                      return (
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          width: '100%',
                                          minHeight: 66,
                                          color: '#6B7280',
                                          fontSize: '13px',
                                          fontWeight: 500
                                        }}>
                                          No common tags found
                                        </div>
                                      );
                                    }
                                    
                                    return (
                                    tagCardsData.map((tag: any) => {
                                      // Calculate percentage change
                                      const currentCount = tag["COUNT(*)"] || 0;

                                      // Get sentiment scores
                                      const currentSentiment = tag["Sentiment Metric"] || 0;

                                      return (
                                        <TagCard
                                          key={tag.tag_array || 'unknown'}
                                          selected={selectedTag === tag.tag_array}
                                          onClick={() => handleTagSelect(tag.tag_array)}
                                          aria-label={`Select tag: ${tag.tag_array}`}
                                          style={{
                                            border: selectedTag === tag.tag_array ? '1px solid #22C55E' : '1px solid #E5E7EB',
                                            boxShadow: '0 1px 6px rgba(16,30,54,0.04)',
                                            width: '175px',
                                            height: '66px',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            minWidth: '150px',
                                            minHeight: '66px',
                                            marginRight: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <TagTitle style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 600 }}>
                                            {tag.tag_array || 'Unknown Tag'}
                                            <span style={{ marginLeft: 6, color: '#EF4444', display: 'flex', alignItems: 'center', fontSize: 16 }}>
                                              <WizrIcon name={currentCount >= 0 ? 'trendUpRed' : 'trendDownGreen'} />
                                              <span style={{
                                                marginLeft: 4,
                                                fontWeight: 500,
                                                fontSize: 13,
                                                color: currentCount >= 0 ? '#22C55E' : '#EF4444'
                                              }}>
                                                {currentCount}
                                              </span>
                                            </span>
                                          </TagTitle>
                                          <div style={{ color: 'rgb(107, 114, 128)', fontSize: 12, fontWeight: 500, marginBottom: 2 }}>
                                            Sentiment Score:{' '}
                                            <span style={{
                                              color: getSentimentColor(currentSentiment),
                                              fontWeight: 600,
                                              fontSize: 12,
                                              marginLeft: 4
                                            }}>
                                              {currentSentiment.toFixed(2)}
                                            </span>
                                          </div>
                                        </TagCard>
                                      );
                                    })
                                    );
                                  })()}
                                </TagsRow>
                              </div>
                            </WizrFlexLayout>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </SummaryTable>
                </WizrFlexLayout>
              </StyledTicketCardForCharts>
            </WizrFlexLayout>

            <RightPanel>
              <ConversationHeader style={{ padding: '0px 0px' }}>
                <FilterPath style={{ padding: '0px 15px' }}>
                  All Conversation
                  {(expandedIntent || selectedIntentName) && (
                    <>
                      <span style={{ margin: '0 4px' }}>›</span>
                      <span>{expandedIntent || selectedIntentName}</span>
                    </>
                  )}
                  {selectedTag && (
                    <>
                      <span style={{ margin: '0 4px' }}>›</span>
                      <span>{selectedTag}</span>
                    </>
                  )}
                  {(expandedIntent || selectedTag) && (
                    <ClearFilters onClick={handleClearAllFilters} style={{ color: '#000', fontWeight: 500, textDecoration: 'underline' }}>
                      Clear All Filters
                    </ClearFilters>
                  )}
                </FilterPath>
                <ConversationStats style={{ padding: '5px 15px' }}>
                  <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>
                    {displayedConversations.length} Conversation{displayedConversations.length !== 1 ? 's' : ''}
                  </span>
                  <StyledDropdown
                    placeHolder="Latest"
                    options={[
                      { label: 'Latest', value: 'latest' },
                      { label: 'Oldest', value: 'oldest' }
                    ]}
                    value={sortOrder}
                    onChange={(value: string) => {
                      setSortOrder(value);
                      // Call with the new sort order
                      fetchConversationsForTagAndIntent(
                        selectedTag,
                        expandedIntent,
                        chartIds?.data?.data?.dashboards,
                        sentiment === 'All' ? undefined : sentiment,  // Only pass sentiment if not 'All'
                        value
                      );
                    }}
                    style={{
                      background: 'white',
                      border: 'none',
                      height: '24px',
                      minHeight: '24px',
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  />
                </ConversationStats>
              </ConversationHeader>
              <ConversationList style={{ padding: '0px 0px' }}>
                {(() => {
                  if (conversationLoading) {
                    return (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px', // Give it some height to center properly
                        width: '100%'
                      }}>
                        <Spin size="small" />
                      </div>
                    );
                  }
                  
                  if (displayedConversations.length === 0) {
                    return (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        width: '100%',
                        color: '#6B7280',
                        fontSize: '14px'
                      }}>
                        No conversations found
                      </div>
                    );
                  }
                  
                  return (
                  <>
                    {(filteredConversations.length > 0 ? filteredConversations : conversationList).map((conversation, index) => (
                      <ConversationCard key={`${conversation.id}-${index}`} bordered={false} style={{ border: 'none', boxShadow: 'none', borderRadius: 'none !important', borderBottomRightRadius: '0px', borderBottomLeftRadius: '0px', borderBottom: '1px solid #E5E7EB', }}>



                        <div style={{ display: 'flex', justifyContent: 'space-between', }}>

                          <div style={{ width: '70%' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <span style={{ fontSize: '13px', color: '#1D1F22', fontWeight: '500', whiteSpace: 'nowrap' }}>Ticket ID :</span>
                              <span style={{ fontSize: '13px', color: '#1D1F22', fontWeight: '500', lineHeight: '20px', }}>{conversation.id}</span>
                              <span style={{ fontSize: '13px', color: '#707276', fontWeight: '400', lineHeight: '20px', }}>{conversation.intent}</span>
                            </div>

                            <div style={{ display: 'flex', gap: '4x', padding: '10px 0px' }}>
                              {conversation.priority && (
                                <StatusTag
                                  key={`priority-${conversation.id}`}
                                  color={getPriorityColor(conversation.priority)}
                                >
                                  {conversation.priority}
                                </StatusTag>
                              )}
                              {conversation.sentiment != null && (
                                <StatusTag
                                  key={`sentiment-${conversation.id}`}
                                  color={getSentimentStatusColor(conversation.sentiment)}
                                >
                                  {conversation.sentiment}
                                </StatusTag>
                              )}
                            </div>


                          </div>


                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', width: '30%', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: 12, color: '#999B9F', fontWeight: 500 }}>{conversation.date}</span>
                            <span><IssueTag>
                              {conversation.type}
                              <span style={{ marginLeft: 4, cursor: 'pointer' }}>
                                {conversation.type === 'Question' && <CxQuestion />}
                                {conversation.type === 'Incident' && <Incident />}
                                {conversation.type === 'Issue' && <Issue />}
                                {conversation.type === 'Task' && <Task />}
                              </span>
                            </IssueTag></span>
                          </div>

                        </div>






                        <div style={{
                          fontWeight: 500,
                          fontSize: 13,
                          color: '#111827',
                          marginBottom: 8,
                          textAlign: 'left',
                          width: '100%'
                        }}>{cleanValue(conversation.title)}</div>
                        <div style={{
                          fontSize: 13,
                          color: '#374151',
                          marginBottom: 12,
                          lineHeight: '1.5',
                          textAlign: 'left',
                          width: '100%'
                        }}>{conversation.description}</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {Array.isArray(conversation.tagArray) &&
                            conversation.tagArray
                              .filter(subTag => subTag && subTag.trim() !== '' && subTag !== 'null')
                              .map((subTag, subTagIndex) => (
                                <TicketTag style={{ backgroundColor: '#EAECF1', color: '#111827', fontWeight: 500, fontSize: '12px', lineHeight: '16px' }} key={`${conversation.id}-tag-${subTag}-${subTagIndex}`}>{subTag}</TicketTag>
                              ))
                          }
                        </div>
                      </ConversationCard>
                    ))}
                  </>
                  );
                })()}
              </ConversationList>
            </RightPanel>
          </WizrFlexLayout>
        </WizrFlexLayout>
      </WizrFlexLayout>
    </div>
  );
}

export default CustomerIntentsAndSentiments;
const StyledTicketCardForCharts = styled(Card) <any>`  background-color: #ffffff;
  width: 100%;
  border-radius: 8px;
  .ant-card-meta-detail {
    border-bottom: none;
    border-top: none;
  }
  .ant-card-head {
    border-bottom: none;
    margin-bottom: 4px;
    text-align: start;
  }
  .ant-card-body {
    text-align: left;
    padding: 0;
  }
`
const StyledTicketCard = styled(Card) <any>`
  background-color: #ffffff;
  width: 100%;
  .ant-card-meta-detail {
    border-bottom: none;
    border-top: none;
  }
  .ant-card-head {
    border-bottom: none;
    text-align: start
  }
  .ant-card-body {
    text-align: center;
    padding: 24px;
  }
`

const StyledTicketResolutionCard = styled(Card) <any>`
  background-color: #ffffff;
  width: 100%;
  .ant-card-meta-detail {
    border-bottom: none;
    border-top: none;
  }
  .ant-card-head {
    border-bottom: none;
    margin-bottom: 4px;
    text-align: start
  }
  .ant-card-body {
    text-align: center;
  }
`

function dedupeFilters(filters: any[]) {
  const seen = new Map();
  // This will keep the last occurrence of each col
  filters.forEach(f => seen.set(f.col, f));
  return Array.from(seen.values());
}